import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/lib/table";
import { actions } from "../slice";
import { AppDispatch } from "../../../app/store";
import { ApiStatuses, SortTypes } from "../../../app/types";
import { LMTable } from "../../../components/LMTable";
import { LMInputNumberIncremental } from "../../../components/LMInputNumberIncremental";
import { ImagePreviewModal } from "../ImagePreviewModal";
import { getTranslation } from "../../i18n";
import { IStoreProduct } from "../types";
import { ProductPhotoCell } from "../../../components";

interface Props {
  list: IStoreProduct[];
  storeProducts: IStoreProduct[];
  status: ApiStatuses;
}

export const ProductsStoreList: FC<Props> = ({
  list,
  status,
  storeProducts,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("store");

  const handleSortNameChange = (column: string, order: SortTypes) => {
    if (column === "name") {
      dispatch(actions.setNameSort(order));
    }
  };

  const handleValueChange = useCallback(
    (value: number, storeProduct: IStoreProduct) => {
      const isProductExists = storeProducts.some(
        (p) => p.productId === storeProduct.productId
      );
      let updatedCartProducts = [];
      if (isProductExists) {
        updatedCartProducts = storeProducts.map((product) => {
          if (product.productId === storeProduct.productId) {
            return {
              ...product,
              inOrder: value,
            };
          }
          return product;
        });
      } else {
        updatedCartProducts = [
          ...storeProducts,
          { ...storeProduct, inOrder: value },
        ];
      }

      const updatedProducts = list.map((product) => {
        if (product.productId === storeProduct.productId) {
          return {
            ...product,
            inOrder: value,
          };
        }
        return product;
      });

      updatedCartProducts = updatedCartProducts.filter((p) => p.inOrder > 0);

      dispatch(actions.updateProducts(updatedProducts));
      dispatch(actions.updateCartProducts(updatedCartProducts));
    },
    [dispatch, storeProducts, list]
  );

  const handleOpenImageToPreview = useCallback(
    (imageId: string) => {
      dispatch(actions.setImageIdToPreview(imageId));
    },
    [dispatch]
  );

  const columns = useMemo(
    () => [
      {
        title: t("name"),
        key: "name",
        render: (storeProduct: IStoreProduct) => {
          return (
            <ProductPhotoCell
              name={getTranslation(storeProduct.product.name) || ""}
              image={storeProduct.product.primaryImageUrl}
            />
          );
        },
        sorter: true,
      },
      {
        title: t("identifier"),
        key: "identifier",
        render: (storeProduct: IStoreProduct) => {
          return (
            <div>
              {storeProduct.product.identifiers.map((identifier) => (
                <div key={identifier.value}>
                  {identifier.type}: {identifier.value}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: t("availableQuantity"),
        key: "availableQuantity",
        dataIndex: "available",
      },
      {
        title: "",
        render: (product: IStoreProduct) => {
          return (
            <LMInputNumberIncremental
              name="list"
              max={product.available > 100 ? 100 : product.available}
              value={product.inOrder}
              onValueChange={(value: number) =>
                handleValueChange(value, product)
              }
            />
          );
        },
      },
    ],
    [t, handleValueChange, handleOpenImageToPreview]
  ) as ColumnType<IStoreProduct>[];

  return (
    <>
      <LMTable
        loading={status === ApiStatuses.loading}
        columns={columns}
        dataSource={list}
        total={{ title: t("total"), amount: list.length }}
        onSortChange={handleSortNameChange}
        rowKey="productId"
      />
      <ImagePreviewModal />
    </>
  );
};
